import { Link } from "@tanstack/react-router";
import { opinlyIdentify, opinlyTrack } from "@/lib/opinly-analytics";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange, Matcher } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, ChevronDown } from "lucide-react";
import {
  getBookedRanges,
  submitBookingInquiry,
  type BookedRange,
} from "@/lib/booking.functions";
import {
  PARSED_RATES,
  parseRates,
  computeQuote,
  applyLastMinuteDiscount,
  FIXED_EXTRAS,
  formatEUR,
  parseLocalISO,
  type Beds,
} from "@/lib/pricing";
import type { Lang } from "@/lib/villa-content";
import { BOOK_T, type BookT } from "@/lib/book-i18n";
import { getPricingSettings } from "@/lib/pricing.functions";
import {
  COUNTRY_DIAL_CODES,
  findCountry,
  stripLeadingDial,
} from "@/lib/country-dial-codes";

const CHECK_INOUT_T: Record<Lang, { title: string; early: string; late: string; hint: string }> = {
  en: {
    title: "Early check-in / late check-out",
    early: "I would like to request an early check-in",
    late: "I would like to request a late check-out",
    hint: "Subject to availability — to be arranged and paid directly with the key keeper.",
  },
  nl: {
    title: "Vroege check-in / late check-out",
    early: "Ik wens een vroege check-in aan te vragen",
    late: "Ik wens een late check-out aan te vragen",
    hint: "Onder voorbehoud van beschikbaarheid — te vereffenen en rechtstreeks te betalen aan de sleutelbeheerder.",
  },
  fr: {
    title: "Arrivée anticipée / départ tardif",
    early: "Je souhaite demander une arrivée anticipée",
    late: "Je souhaite demander un départ tardif",
    hint: "Sous réserve de disponibilité — à régler et à payer directement auprès du gardien des clés.",
  },
  de: {
    title: "Frühes Check-in / spätes Check-out",
    early: "Ich möchte ein frühes Check-in anfragen",
    late: "Ich möchte ein spätes Check-out anfragen",
    hint: "Je nach Verfügbarkeit — direkt mit dem Schlüsselverwalter zu vereinbaren und zu bezahlen.",
  },
};

export type BookSearch = {
  from?: string;
  to?: string;
  bedrooms?: number;
};

export function validateBookSearch(raw: Record<string, unknown>): BookSearch {
  const fromS = typeof raw.from === "string" ? raw.from : undefined;
  const toS = typeof raw.to === "string" ? raw.to : undefined;
  const bedsRaw = raw.bedrooms;
  const beds =
    typeof bedsRaw === "number"
      ? bedsRaw
      : typeof bedsRaw === "string"
        ? Number(bedsRaw)
        : undefined;
  return {
    from: fromS && parseLocalISO(fromS) ? fromS : undefined,
    to: toS && parseLocalISO(toS) ? toS : undefined,
    bedrooms: beds && [2, 3, 4].includes(beds) ? beds : undefined,
  };
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | {
      kind: "success";
      reference: string;
      message: string;
      summary: {
        checkIn: string;
        checkOut: string;
        nights: number;
        bedrooms: number;
        adults: number;
        children: number;
        babies: number;
        totalGuests: number;
        grandTotal: number | null;
      };
    }
  | { kind: "error"; message: string };

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseISO(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function nightsBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function bookedDateMatchers(ranges: BookedRange[]) {
  return ranges.map((r) => {
    const from = parseISO(r.start);
    const to = parseISO(r.end);
    to.setDate(to.getDate() - 1);
    return { from, to };
  });
}

function bookedDateMatchersByStatus(ranges: BookedRange[], status: "pending" | "confirmed") {
  return bookedDateMatchers(ranges.filter((r) => r.status === status));
}

function middleDateMatchers(ranges: BookedRange[]) {
  const out: { from: Date; to: Date }[] = [];
  for (const r of ranges) {
    const from = parseISO(r.start);
    from.setDate(from.getDate() + 1);
    const to = parseISO(r.end);
    to.setDate(to.getDate() - 1);
    if (from <= to) out.push({ from, to });
  }
  return out;
}

function middleDateMatchersByStatus(ranges: BookedRange[], status: "pending" | "confirmed") {
  return middleDateMatchers(ranges.filter((r) => r.status === status));
}

function arrivalDatesByStatus(ranges: BookedRange[], status: "pending" | "confirmed"): Date[] {
  return ranges.filter((r) => r.status === status).map((r) => parseISO(r.start));
}

function departureDatesByStatus(ranges: BookedRange[], status: "pending" | "confirmed"): Date[] {
  return ranges.filter((r) => r.status === status).map((r) => parseISO(r.end));
}

function rangeOverlapsBooked(
  range: { from: Date; to: Date },
  booked: BookedRange[],
): boolean {
  const startISO = toISO(range.from);
  const endISO = toISO(range.to);
  return booked.some((b) => startISO < b.end && endISO > b.start);
}

type NavigateArgs = { from?: string; to?: string; bedrooms: number };

export function BookPage({
  lang,
  search,
  syncUrl,
  homeHref,
}: {
  lang: Lang;
  search: BookSearch;
  /** Replace URL with current state so links stay shareable. */
  syncUrl: (next: NavigateArgs) => void;
  /** A Link element pointing at the language home (for the header). */
  homeHref: React.ReactNode;
}) {
  const t = BOOK_T[lang];
  const submit = useServerFn(submitBookingInquiry);
  const fetchRanges = useServerFn(getBookedRanges);
  const fetchPricing = useServerFn(getPricingSettings);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = search.from ? parseLocalISO(search.from) ?? undefined : undefined;
    const to = search.to ? parseLocalISO(search.to) ?? undefined : undefined;
    return from && to ? { from, to } : from ? { from, to: undefined } : undefined;
  });
  const [bedrooms, setBedrooms] = useState<Beds>(
    (search.bedrooms as Beds | undefined) ?? 4,
  );
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  const [babies, setBabies] = useState<number>(0);
  const [country, setCountry] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [gdprConsent, setGdprConsent] = useState<boolean>(false);
  const [pendingConfirm, setPendingConfirm] = useState<null | {
    payload: Record<string, unknown>;
    form: HTMLFormElement;
    earlyCheckIn: boolean;
    lateCheckOut: boolean;
    messageText: string;
  }>(null);
  const guests = adults + children;
  const totalGuests = adults + children + babies;
  const guestsOverLimit = totalGuests > 8;
  const guestLimitMessage =
    lang === "nl"
      ? `Maximaal 8 gasten in totaal (volwassenen + kinderen + baby's). Je hebt er nu ${totalGuests} geselecteerd.`
      : lang === "fr"
      ? `Maximum 8 personnes au total (adultes + enfants + bébés). Vous en avez sélectionné ${totalGuests}.`
      : lang === "de"
      ? `Maximal 8 Gäste insgesamt (Erwachsene + Kinder + Babys). Aktuell ausgewählt: ${totalGuests}.`
      : `Maximum 8 guests in total (adults + children + babies). You currently selected ${totalGuests}.`;

  // Rule: max 2 personen (volwassenen + kinderen) + 1 baby per slaapkamer.
  const minBedrooms = Math.min(
    4,
    Math.max(2, Math.ceil((adults + children) / 2), babies),
  ) as Beds;
  useEffect(() => {
    if (bedrooms < minBedrooms) setBedrooms(minBedrooms);
  }, [minBedrooms, bedrooms]);
  const bedroomsBelowMin = bedrooms < minBedrooms;
  const bedroomsMinMessage =
    lang === "nl"
      ? `Voor ${adults + children} personen en ${babies} baby's zijn minstens ${minBedrooms} slaapkamers nodig (max. 2 personen + 1 baby per kamer).`
      : lang === "fr"
      ? `Pour ${adults + children} personnes et ${babies} bébés, ${minBedrooms} chambres minimum sont requises (max. 2 personnes + 1 bébé par chambre).`
      : lang === "de"
      ? `Für ${adults + children} Personen und ${babies} Babys sind mindestens ${minBedrooms} Schlafzimmer erforderlich (max. 2 Personen + 1 Baby pro Zimmer).`
      : `For ${adults + children} people and ${babies} babies you need at least ${minBedrooms} bedrooms (max. 2 people + 1 baby per room).`;

  useEffect(() => {
    syncUrl({
      from: range?.from ? toISO(range.from) : undefined,
      to: range?.to ? toISO(range.to) : undefined,
      bedrooms,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.from?.getTime(), range?.to?.getTime(), bedrooms]);

  const { data: allRanges = [], refetch: refetchBooked } = useQuery({
    queryKey: ["booked-ranges"],
    queryFn: () => fetchRanges(),
    staleTime: 60_000,
  });
  // "Op aanvraag" blocks stay selectable: they only get a distinct look.
  const booked = useMemo(
    () => allRanges.filter((r) => r.status !== "on_request"),
    [allRanges],
  );
  const onRequestMatchers = useMemo(
    () =>
      allRanges
        .filter((r) => r.status === "on_request")
        .map((r) => {
          const from = parseISO(r.start);
          const to = parseISO(r.end);
          to.setDate(to.getDate() - 1);
          return { from, to };
        }),
    [allRanges],
  );

  const { data: pricing } = useQuery({
    queryKey: ["pricing-settings"],
    queryFn: () => fetchPricing(),
    staleTime: 5 * 60_000,
  });

  const activeParsedRates = useMemo(
    () => (pricing && pricing.rates.length ? parseRates(pricing.rates) : PARSED_RATES),
    [pricing],
  );
  const lastMinuteLabel = useMemo(() => {
    if (!pricing) return "Last-minute deal";
    const l = pricing.lastMinuteLabel as Record<string, string>;
    return l[lang] ?? l.en ?? "Last-minute deal";
  }, [pricing, lang]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const minCheckIn = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 5);
    return d;
  }, [today]);

  const leadOverrides = useMemo(
    () =>
      ((pricing?.leadTimeOverrides ?? []) as Array<{
        start: string;
        end: string;
        enabled?: boolean;
      }>).filter((r) => r.enabled !== false),
    [pricing],
  );
  const isOverrideDate = useMemo(() => {
    return (date: Date) => {
      if (leadOverrides.length === 0) return false;
      const iso = toISO(date);
      return leadOverrides.some((r) => iso >= r.start && iso < r.end);
    };
  }, [leadOverrides]);
  const isRangeFullyInOverride = useMemo(() => {
    return (from: Date, to: Date) => {
      if (leadOverrides.length === 0) return false;
      const startISO = toISO(from);
      const endISO = toISO(to);
      return leadOverrides.some((r) => startISO >= r.start && endISO <= r.end);
    };
  }, [leadOverrides]);

  // A day that falls inside a CONFIRMED booking can never be re-opened by an
  // admin override — once a booking is confirmed the period is truly taken.
  const isConfirmedBookedDate = useMemo(() => {
    const confirmed = booked.filter((b) => b.status === "confirmed");
    return (date: Date) => {
      const iso = toISO(date);
      return confirmed.some((b) => iso > b.start && iso < b.end);
    };
  }, [booked]);
  const rangeOverlapsConfirmed = useMemo(() => {
    const confirmed = booked.filter((b) => b.status === "confirmed");
    return (from: Date, to: Date) => {
      const startISO = toISO(from);
      const endISO = toISO(to);
      return confirmed.some((b) => startISO < b.end && endISO > b.start);
    };
  }, [booked]);

  // Any day that is the check-in or check-out day of an existing booking is
  // a valid turnover day — you can check out that morning before the next
  // guest arrives, or check in that afternoon after the previous guest leaves.
  // We treat these as always selectable (except when they're in the past).
  const isTurnoverDate = useMemo(() => {
    const starts = new Set<string>();
    const ends = new Set<string>();
    for (const r of booked) {
      starts.add(r.start);
      ends.add(r.end);
    }
    // A day is a valid turnover only when it's a check-in OR check-out but
    // not both — if another booking already checks in on the same day the
    // previous one checks out, the day is fully occupied and unavailable.
    return (date: Date) => {
      const iso = toISO(date);
      const isStart = starts.has(iso);
      const isEnd = ends.has(iso);
      return (isStart || isEnd) && !(isStart && isEnd);
    };
  }, [booked]);

  const isFullyOccupiedTurnover = useMemo(() => {
    const starts = new Set<string>();
    const ends = new Set<string>();
    for (const r of booked) {
      starts.add(r.start);
      ends.add(r.end);
    }
    return (date: Date) => {
      const iso = toISO(date);
      return starts.has(iso) && ends.has(iso);
    };
  }, [booked]);

  const disabledMatchers = useMemo(
    (): Matcher[] => [
      // Past days are always disabled; between today and the 5-day cutoff,
      // only dates covered by an admin override are selectable.
      (date: Date) =>
        date < today ||
        (date < minCheckIn && (!isOverrideDate(date) || isConfirmedBookedDate(date))),
      // A day that is both a check-in of one booking and a check-out of
      // another is fully occupied — disable it regardless of overrides.
      (date: Date) => isFullyOccupiedTurnover(date),
      // Middle days of existing bookings are disabled UNLESS the day is
      // covered by an admin override (admin explicitly opened it up).
      (date: Date) => {
        if (isOverrideDate(date) && !isConfirmedBookedDate(date)) return false;
        return middleDateMatchers(booked).some(
          (r) => date >= r.from && date <= r.to,
        );
      },
      // July & August: only Saturdays are selectable (Sat-to-Sat bookings),
      // but turnover days (existing booking check-in / check-out) stay open
      // so guests can end their stay on the same day the next guest arrives.
      (date: Date) => {
        const m = date.getMonth();
        return (
          (m === 6 || m === 7) &&
          date.getDay() !== 6 &&
          !(isOverrideDate(date) && !isConfirmedBookedDate(date)) &&
          !isTurnoverDate(date)
        );
      },
    ],
    [
      booked,
      today,
      minCheckIn,
      isOverrideDate,
      isTurnoverDate,
      isFullyOccupiedTurnover,
      isConfirmedBookedDate,
    ],
  );

  const confirmedMatchers = useMemo(() => bookedDateMatchersByStatus(booked, "confirmed"), [booked]);
  const pendingMatchers = useMemo(() => bookedDateMatchersByStatus(booked, "pending"), [booked]);
  const confirmedMiddle = useMemo(() => middleDateMatchersByStatus(booked, "confirmed"), [booked]);
  const pendingMiddle = useMemo(() => middleDateMatchersByStatus(booked, "pending"), [booked]);
  const confirmedArrival = useMemo(() => arrivalDatesByStatus(booked, "confirmed"), [booked]);
  const pendingArrival = useMemo(() => arrivalDatesByStatus(booked, "pending"), [booked]);
  const confirmedDeparture = useMemo(() => departureDatesByStatus(booked, "confirmed"), [booked]);
  const pendingDeparture = useMemo(() => departureDatesByStatus(booked, "pending"), [booked]);

  const selectionOverlaps =
    range?.from && range?.to
      ? (rangeOverlapsConfirmed(range.from, range.to) ||
          !isRangeFullyInOverride(range.from, range.to)) &&
        rangeOverlapsBooked({ from: range.from, to: range.to }, booked)
      : false;

  const nights = range?.from && range?.to ? nightsBetween(range.from, range.to) : 0;
  const allowShortStay =
    !!range?.from &&
    !!range?.to &&
    isRangeFullyInOverride(range.from, range.to) &&
    !rangeOverlapsConfirmed(range.from, range.to);

  const quote = useMemo(() => {
    if (!range?.from || !range?.to) return null;
    const base = computeQuote(range.from, range.to, bedrooms, activeParsedRates);
    if (!pricing) return base;
    return applyLastMinuteDiscount(base, range.from, {
      enabled: pricing.lastMinuteEnabled,
      percent: pricing.lastMinutePercent,
      withinDays: pricing.lastMinuteWithinDays,
      label: lastMinuteLabel,
    });
  }, [range?.from, range?.to, bedrooms, activeParsedRates, pricing, lastMinuteLabel]);

  const linenTotal = guests * FIXED_EXTRAS.linenPerPerson;
  const babyPackTotal = babies > 0 ? FIXED_EXTRAS.babyPack : 0;
  const grandTotal =
    quote?.rentalSubtotal != null
      ? quote.rentalSubtotal + FIXED_EXTRAS.cleaning + linenTotal + babyPackTotal - (quote.discount?.amount ?? 0)
      : null;

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  const phoneDigits = phone.replace(/\D/g, "");
  const contactValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    emailValid &&
    country.trim().length > 0 &&
    phoneDigits.length >= 5;
  const canSubmit =
    !!range?.from &&
    !!range?.to &&
    (nights >= 7 || allowShortStay) &&
    !selectionOverlaps &&
    !guestsOverLimit &&
    !bedroomsBelowMin &&
    contactValid &&
    gdprConsent &&
    status.kind !== "submitting";

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      setStatus({ kind: "error", message: t.err.pickDates });
      return;
    }
    if (selectionOverlaps) {
      setStatus({ kind: "error", message: t.err.overlap });
      return;
    }
    if (nights < 7 && !allowShortStay) {
      setStatus({
        kind: "error",
        message:
          lang === "nl"
            ? "Minimum verblijf is 7 nachten."
            : lang === "fr"
            ? "Le séjour minimum est de 7 nuits."
            : lang === "de"
            ? "Mindestaufenthalt beträgt 7 Nächte."
            : "Minimum stay is 7 nights.",
      });
      return;
    }
    if (adults + children + babies > 8) {
      setStatus({
        kind: "error",
        message: guestLimitMessage,
      });
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = Object.fromEntries(fd.entries());
    const fName = String(payload.firstName ?? "").trim();
    const lName = String(payload.lastName ?? "").trim();
    payload.firstName = fName;
    payload.lastName = lName;
    payload.name = [fName, lName].filter(Boolean).join(" ");
    payload.checkIn = toISO(range.from);
    payload.checkOut = toISO(range.to);
    payload.lang = lang;
    // Attach the quote the guest is seeing so admins can reuse it.
    if (quote?.rentalSubtotal != null) {
      const extrasCents =
        (FIXED_EXTRAS.cleaning + linenTotal + babyPackTotal) * 100;
      const discountCents = (quote.discount?.amount ?? 0) * 100;
      payload.quotedRentalCents = String(quote.rentalSubtotal * 100);
      payload.quotedExtrasCents = String(extrasCents);
      payload.quotedDiscountCents = String(discountCents);
      payload.quotedTotalCents = String(((grandTotal ?? 0) * 100).toFixed(0));
      payload.quotedCurrency = "eur";
      payload.quotedBreakdown = JSON.stringify({
        nights: quote.nights,
        bedrooms,
        adults,
        children,
        babies,
        segments: quote.segments,
        rentalSubtotal: quote.rentalSubtotal,
        cleaning: FIXED_EXTRAS.cleaning,
        linenTotal,
        babyPackTotal,
        discount: quote.discount ?? null,
        grandTotal,
      });
    }
    setPendingConfirm({
      payload,
      form,
      earlyCheckIn: !!payload.earlyCheckIn,
      lateCheckOut: !!payload.lateCheckOut,
      messageText: String(payload.message ?? ""),
    });
  }

  async function performSubmit() {
    if (!pendingConfirm || !range?.from || !range?.to) return;
    const { payload, form } = pendingConfirm;
    setPendingConfirm(null);
    setStatus({ kind: "submitting" });
    try {
      const res = await submit({ data: payload });
      if (res.ok) {
        opinlyIdentify({ email });
        opinlyTrack("generate_lead", {
          checkIn: toISO(range.from),
          checkOut: toISO(range.to),
          nights,
          value: grandTotal ?? undefined,
          currency: "EUR",
        }, { externalEventId: res.reference, email });
        setStatus({
          kind: "success",
          reference: res.reference,
          message: res.message,
          summary: {
            checkIn: toISO(range.from),
            checkOut: toISO(range.to),
            nights,
            bedrooms,
            adults,
            children,
            babies,
            totalGuests: adults + children + babies,
            grandTotal: grandTotal ?? null,
          },
        });
        form.reset();
        setRange(undefined);
        setFirstName("");
        setLastName("");
        setEmail("");
        setCountry("");
        setPhone("");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setStatus({ kind: "error", message: res.message });
        if (res.conflict) void refetchBooked();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message || t.err.invalidForm
          : t.err.unexpected;
      setStatus({ kind: "error", message });
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/70 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          {homeHref}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-sm font-medium uppercase tracking-[0.2em] text-primary">
          {t.inquiryEyebrow}
        </p>
        <h1 className="mt-3 text-4xl font-semibold sm:text-5xl">{t.pageTitle}</h1>
        <p className="mt-4 max-w-xl text-lg text-muted-foreground">{t.pageIntro}</p>

        {status.kind === "success" && (
          <SuccessCard lang={lang} t={t} status={status} />
        )}

        {status.kind === "error" && (
          <div className="mt-10 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-destructive">
            <p className="font-medium">{t.errorTitle}</p>
            <p className="mt-1 text-sm">{status.message}</p>
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="mt-10 space-y-8 rounded-2xl border border-border/70 bg-card p-6 shadow-sm sm:p-10"
        >
          <Fieldset legend={t.fsStay}>
            <AvailabilityCalendar
              t={t}
              range={range}
              onChange={setRange}
              disabled={disabledMatchers}
              nights={nights}
              overlaps={selectionOverlaps}
              allowShortStay={allowShortStay}
              confirmedMatchers={confirmedMatchers}
              onRequestMatchers={onRequestMatchers}
              pendingMatchers={pendingMatchers}
              confirmedMiddle={confirmedMiddle}
              pendingMiddle={pendingMiddle}
              confirmedArrival={confirmedArrival}
              pendingArrival={pendingArrival}
              confirmedDeparture={confirmedDeparture}
              pendingDeparture={pendingDeparture}
              locale={lang}
              rates={activeParsedRates}
            />
            <p className="rounded-lg border border-border/70 bg-muted/40 p-3 text-sm text-muted-foreground">
              {lang === "nl"
                ? "Gelieve de juiste informatie in te vullen zodat de berekening zo correct mogelijk is (max. 2 personen + 1 baby per slaapkamer)."
                : lang === "fr"
                  ? "Merci d'indiquer les informations exactes afin que le calcul soit le plus correct possible (max. 2 personnes + 1 bébé par chambre)."
                  : lang === "de"
                    ? "Bitte geben Sie die korrekten Angaben ein, damit die Berechnung so genau wie möglich ist (max. 2 Personen + 1 Baby pro Schlafzimmer)."
                    : "Please fill in the correct information so the calculation is as accurate as possible (max. 2 people + 1 baby per bedroom)."}
            </p>
            <p className="rounded-lg border border-amber-500/40 bg-amber-400/10 p-3 text-sm text-foreground">
              {lang === "nl"
                ? "Staan jouw gewenste data in optie (geel)? Stuur ons gerust een WhatsApp-bericht — vaak komen die dagen alsnog vrij."
                : lang === "fr"
                  ? "Vos dates souhaitées sont en option (jaune) ? Envoyez-nous un message WhatsApp — ces jours se libèrent souvent."
                  : lang === "de"
                    ? "Sind Ihre Wunschdaten in Option (gelb)? Schreiben Sie uns gerne eine WhatsApp-Nachricht — diese Tage werden oft wieder frei."
                    : "Are your preferred dates on option (yellow)? Feel free to send us a WhatsApp message — those days often become available again."}{" "}
              <a
                href="https://wa.me/32475921693"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-4"
              >
                {lang === "nl"
                  ? "WhatsApp ons"
                  : lang === "fr"
                    ? "Nous envoyer un WhatsApp"
                    : lang === "de"
                      ? "WhatsApp senden"
                      : "WhatsApp us"}
              </a>
            </p>
            <Row>
              <ControlledSelect
                label={t.bedrooms}
                name="bedrooms"
                required
                options={[2, 3, 4].filter((n) => n >= minBedrooms)}
                value={bedrooms}
                onValueChange={(v: number) => setBedrooms(v as Beds)}
              />
              <ControlledSelect
                label={t.adults}
                name="adults"
                required
                options={[1, 2, 3, 4, 5, 6, 7, 8]}
                value={adults}
                onValueChange={setAdults}
              />
            </Row>
            <Row>
              <ControlledSelect
                label={t.children}
                name="children"
                required
                options={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
                value={children}
                onValueChange={setChildren}
              />
              <ControlledSelect
                label={t.babies}
                name="babies"
                required
                options={[0, 1, 2]}
                value={babies}
                onValueChange={setBabies}
              />
            </Row>
            {guestsOverLimit && (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm font-medium text-destructive"
              >
                {guestLimitMessage}
              </div>
            )}
            {!guestsOverLimit && bedroomsBelowMin && (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm font-medium text-destructive"
              >
                {bedroomsMinMessage}
              </div>
            )}
            <QuoteCard
              t={t}
              quote={quote}
              bedrooms={bedrooms}
              guests={guests}
              adults={adults}
              children={children}
              babies={babies}
              linenTotal={linenTotal}
              babyPackTotal={babyPackTotal}
              grandTotal={grandTotal}
              overlaps={selectionOverlaps}
            />
          </Fieldset>

          <Fieldset legend={t.fsContact}>
            <Row>
              <Field label={t.firstName} name="firstName" required maxLength={60} autoComplete="given-name" value={firstName} onChange={setFirstName} />
              <Field label={t.lastName} name="lastName" required maxLength={80} autoComplete="family-name" value={lastName} onChange={setLastName} />
            </Row>
            <Row>
              <Field label={t.email} name="email" type="email" required maxLength={255} autoComplete="email" value={email} onChange={setEmail} />
            </Row>
            <Row>
              <CountryField
                label={t.country}
                value={country}
                onChange={(next) => {
                  setCountry(next);
                  const match = findCountry(next);
                  if (match) {
                    const rest = stripLeadingDial(phone);
                    setPhone(rest ? `${match.dial} ${rest}` : `${match.dial} `);
                  }
                }}
              />
              <PhoneField
                label={t.phone}
                value={phone}
                onChange={setPhone}
              />
            </Row>
          </Fieldset>

          <Fieldset legend={t.fsElse}>
            <label className="block">
              <span className="text-sm font-medium text-foreground">{t.messageLabel}</span>
              <textarea
                name="message"
                rows={5}
                maxLength={2000}
                placeholder={t.messagePlaceholder}
                className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
              />
            </label>
            <div className="mt-4 rounded-lg border border-border bg-card p-4 text-sm space-y-2">
              <p className="font-medium text-foreground">{CHECK_INOUT_T[lang].title}</p>
              <label className="flex items-center gap-2">
                <input type="checkbox" name="earlyCheckIn" />
                <span>{CHECK_INOUT_T[lang].early}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" name="lateCheckOut" />
                <span>{CHECK_INOUT_T[lang].late}</span>
              </label>
              <p className="text-xs text-muted-foreground">{CHECK_INOUT_T[lang].hint}</p>
            </div>
          </Fieldset>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border/70 pt-6">
            <div className="space-y-2">
              {t.guestInfoRequired && (
                <p className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
                  {t.guestInfoRequired}
                </p>
              )}
              <p className="text-xs text-muted-foreground">{t.consent}</p>
              <label className="flex items-start gap-2 text-xs text-foreground">
                <input
                  type="checkbox"
                  name="gdprConsent"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                  checked={gdprConsent}
                  onChange={(e) => setGdprConsent(e.target.checked)}
                  required
                />
                <span>
                  {lang === "nl"
                    ? <>Ik ga akkoord met de verwerking van mijn gegevens voor deze aanvraag conform de <Link to="/trust" className="underline">privacyverklaring</Link>. <span className="text-primary">*</span></>
                    : lang === "fr"
                    ? <>J'accepte le traitement de mes données pour cette demande conformément à la <Link to="/trust" className="underline">politique de confidentialité</Link>. <span className="text-primary">*</span></>
                    : lang === "de"
                    ? <>Ich stimme der Verarbeitung meiner Daten für diese Anfrage gemäß der <Link to="/trust" className="underline">Datenschutzerklärung</Link> zu. <span className="text-primary">*</span></>
                    : <>I agree to the processing of my data for this inquiry as described in the <Link to="/trust" className="underline">privacy notice</Link>. <span className="text-primary">*</span></>}
                </span>
              </label>
            </div>
            <button
              type="submit"
              id="send-inquiry"
              disabled={!canSubmit}
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-medium text-primary-foreground shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status.kind === "submitting" ? t.sending : t.send}
            </button>
          </div>
        </form>
      </main>

      {range?.from && range?.to && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-primary/30 bg-primary/95 px-4 py-3 text-primary-foreground shadow-lg backdrop-blur md:hidden">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <div className="text-sm">
              <p className="font-medium tabular-nums">
                {t.sticky.summary(nights, guests)}
                {grandTotal != null && <> · {t.sticky.est(formatEUR(grandTotal))}</>}
              </p>
            </div>
            <button
              type="button"
              aria-label={t.sticky.scrollAria}
              onClick={() => {
                document
                  .getElementById("send-inquiry")
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/15 transition hover:bg-primary-foreground/25"
            >
              <ChevronDown className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      )}

      {pendingConfirm && (
        <ConfirmInquiryModal
          lang={lang}
          firstName={firstName}
          lastName={lastName}
          email={email}
          country={country}
          phone={phone}
          checkIn={range?.from ? toISO(range.from) : ""}
          checkOut={range?.to ? toISO(range.to) : ""}
          nights={nights}
          bedrooms={bedrooms}
          adults={adults}
          children={children}
          babies={babies}
          earlyCheckIn={pendingConfirm.earlyCheckIn}
          lateCheckOut={pendingConfirm.lateCheckOut}
          messageText={pendingConfirm.messageText}
          grandTotal={grandTotal}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => void performSubmit()}
          submitting={status.kind === "submitting"}
        />
      )}
    </div>
  );
}

function AvailabilityCalendar({
  t,
  range,
  onChange,
  disabled,
  nights,
  overlaps,
  allowShortStay,
  confirmedMatchers,
  onRequestMatchers,
  pendingMatchers,
  confirmedMiddle,
  pendingMiddle,
  confirmedArrival,
  pendingArrival,
  confirmedDeparture,
  pendingDeparture,
  locale,
  rates,
}: {
  t: BookT;
  range: DateRange | undefined;
  onChange: (r: DateRange | undefined) => void;
  disabled: Matcher[];
  nights: number;
  overlaps: boolean;
  allowShortStay: boolean;
  confirmedMatchers: { from: Date; to: Date }[];
  onRequestMatchers: { from: Date; to: Date }[];
  pendingMatchers: { from: Date; to: Date }[];
  confirmedMiddle: { from: Date; to: Date }[];
  pendingMiddle: { from: Date; to: Date }[];
  confirmedArrival: Date[];
  pendingArrival: Date[];
  confirmedDeparture: Date[];
  pendingDeparture: Date[];
  locale: string;
  rates: typeof PARSED_RATES;
}) {
  return (
    <div className="rounded-xl border border-border/70 bg-background p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{t.cal.pickDates}</p>
          <p className="text-xs text-muted-foreground">{t.cal.explanation}</p>
        </div>
        <Legend t={t} />
      </div>

      <div className="mt-4 flex justify-center">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={range}
          onSelect={onChange}
          disabled={disabled}
          className={cn("pointer-events-auto")}
          modifiers={{
            onRequest: onRequestMatchers,
            confirmed: confirmedMiddle,
            pending: pendingMiddle,
            confirmedArrival,
            pendingArrival,
            confirmedDeparture,
            pendingDeparture,
            // A day that is both a departure (of one booking) and an
            // arrival (of another) — render both halves filled.
            confirmedTurnover: (date) => {
              const iso = toISO(date);
              const isArr = confirmedArrival.some((d) => toISO(d) === iso);
              const isDep = confirmedDeparture.some((d) => toISO(d) === iso);
              return isArr && isDep;
            },
            pendingTurnover: (date) => {
              const iso = toISO(date);
              const isArr = pendingArrival.some((d) => toISO(d) === iso);
              const isDep = pendingDeparture.some((d) => toISO(d) === iso);
              return isArr && isDep;
            },
            mixedTurnover: (date) => {
              const iso = toISO(date);
              const confArr = confirmedArrival.some((d) => toISO(d) === iso);
              const confDep = confirmedDeparture.some((d) => toISO(d) === iso);
              const pendArr = pendingArrival.some((d) => toISO(d) === iso);
              const pendDep = pendingDeparture.some((d) => toISO(d) === iso);
              return (confDep && pendArr) || (pendDep && confArr);
            },
            available: (date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (date < today) return false;
              // Only the middle nights of an existing booking are truly
              // unavailable. Arrival and departure days are shared "turnover"
              // days: a new guest can check out on someone else's check-in
              // day and check in on someone else's check-out day.
              const inMiddle = [...confirmedMiddle, ...pendingMiddle].some(
                (m) => date >= m.from && date <= m.to,
              );
              return !inMiddle;
            },
          }}
          modifiersClassNames={{
            onRequest:
              "!text-foreground rounded-md font-medium [background:repeating-linear-gradient(135deg,rgba(120,113,108,0.28)_0_4px,transparent_4px_8px)]",
            confirmed:
              "!bg-red-500/20 !text-red-700 dark:!text-red-300 line-through rounded-md cursor-not-allowed",
            pending:
              "!bg-yellow-400/30 !text-yellow-800 dark:!text-yellow-200 line-through rounded-md cursor-not-allowed",
            confirmedArrival:
              "!text-green-800 dark:!text-green-300 rounded-md cursor-pointer",
            confirmedDeparture:
              "!text-green-800 dark:!text-green-300 rounded-md cursor-pointer",
            pendingArrival:
              "!text-green-800 dark:!text-green-300 rounded-md cursor-pointer",
            pendingDeparture:
              "!text-green-800 dark:!text-green-300 rounded-md cursor-pointer",
            confirmedTurnover:
              "!text-red-700 dark:!text-red-300 line-through cursor-not-allowed",
            pendingTurnover:
              "!text-yellow-800 dark:!text-yellow-200 line-through cursor-not-allowed",
            mixedTurnover:
              "!text-red-700 dark:!text-red-300 line-through cursor-not-allowed",
            available: "bg-green-500/10 text-green-800 dark:text-green-300 rounded-md",
          }}
          modifiersStyles={{
            confirmedArrival: {
              background:
                "linear-gradient(135deg, transparent 0 50%, rgba(239,68,68,0.25) 50% 100%)",
              borderRadius: "0.375rem",
            },
            confirmedDeparture: {
              background:
                "linear-gradient(135deg, rgba(239,68,68,0.25) 0 50%, transparent 50% 100%)",
              borderRadius: "0.375rem",
            },
            pendingArrival: {
              background:
                "linear-gradient(135deg, transparent 0 50%, rgba(250,204,21,0.35) 50% 100%)",
              borderRadius: "0.375rem",
            },
            pendingDeparture: {
              background:
                "linear-gradient(135deg, rgba(250,204,21,0.35) 0 50%, transparent 50% 100%)",
              borderRadius: "0.375rem",
            },
            confirmedTurnover: {
              background:
                "linear-gradient(135deg, rgba(239,68,68,0.25) 0 50%, rgba(239,68,68,0.25) 50% 100%)",
              borderRadius: "0.375rem",
            },
            pendingTurnover: {
              background:
                "linear-gradient(135deg, rgba(250,204,21,0.35) 0 50%, rgba(250,204,21,0.35) 50% 100%)",
              borderRadius: "0.375rem",
            },
            mixedTurnover: {
              background:
                "linear-gradient(135deg, rgba(239,68,68,0.25) 0 50%, rgba(250,204,21,0.35) 50% 100%)",
              borderRadius: "0.375rem",
            },
          }}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4 text-sm">
        {range?.from && range?.to ? (
          <p className="text-foreground">
            <span className="font-medium">{range.from.toLocaleDateString(locale)}</span>
            {" → "}
            <span className="font-medium">{range.to.toLocaleDateString(locale)}</span>
            <span className="text-muted-foreground"> · {t.cal.nights(nights)}</span>
          </p>
        ) : (
          <p className="text-muted-foreground">{t.cal.noDates}</p>
        )}

        {overlaps && (
          <p className="text-sm font-medium text-destructive">{t.cal.overlapWarning}</p>
        )}

        {range?.from && range?.to && nights > 0 && nights < 7 && !overlaps && !allowShortStay && (
          <p className="text-sm font-medium text-destructive">
            {locale === "nl"
              ? "Minimum verblijf is 7 nachten."
              : locale === "fr"
              ? "Le séjour minimum est de 7 nuits."
              : locale === "de"
              ? "Mindestaufenthalt beträgt 7 Nächte."
              : "Minimum stay is 7 nights."}
          </p>
        )}

        {(range?.from || range?.to) && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="ml-auto rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            {t.cal.clear}
          </button>
        )}
      </div>

      <RatePeriodLegend t={t} rates={rates} />
    </div>
  );
}

function RatePeriodLegend({ t, rates }: { t: BookT; rates: typeof PARSED_RATES }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-border/60 pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
      >
        <span>{open ? t.cal.hideWeekly : t.cal.showWeekly}</span>
        <span aria-hidden className={cn("transition-transform duration-300", open && "rotate-180")}>
          ▾
        </span>
      </button>
      <div
        className={cn(
          "grid transition-all duration-300 ease-out",
          open ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-2 overflow-x-auto rounded-md bg-secondary/40">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">{t.cal.bedroomsLine.split("·")[0]?.trim() ? "" : ""}</th>
                  {t.cal.bedroomsLine.split("·").map((h) => (
                    <th key={h} className="px-3 py-2 text-right font-medium whitespace-nowrap">{h.trim()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.label} className="border-t border-border/40">
                    <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{r.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {r.beds4 != null ? formatEUR(r.beds4) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {r.beds3 != null ? formatEUR(r.beds3) : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {r.beds2 != null ? formatEUR(r.beds2) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: BookT }) {
  return (
    <ul aria-label={t.leg.legendAria} className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-3 rounded-sm bg-green-500/30 border border-green-600/40" />
        {t.leg.available}
      </li>
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-3 rounded-sm bg-yellow-400/60 border border-yellow-600/50" />
        {t.leg.reserved}
      </li>
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-3 rounded-sm bg-red-500/50 border border-red-600/60" />
        {t.leg.unavailable}
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="inline-block size-3 rounded-sm border border-red-600/40"
          style={{
            background:
              "linear-gradient(135deg, rgba(239,68,68,0.45) 0 50%, transparent 50% 100%)",
          }}
        />
        {t.leg.checkoutMorning}
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="inline-block size-3 rounded-sm border border-red-600/40"
          style={{
            background:
              "linear-gradient(135deg, transparent 0 50%, rgba(239,68,68,0.45) 50% 100%)",
          }}
        />
        {t.leg.checkinAfternoon}
      </li>
      <li className="flex items-center gap-1.5">
        <span className="inline-block size-3 rounded-sm bg-primary" />
        {t.leg.selected}
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="inline-block size-3 rounded-sm border border-muted-foreground/40"
          style={{
            background:
              "repeating-linear-gradient(135deg, rgba(120,113,108,0.35) 0 3px, transparent 3px 6px)",
          }}
        />
        {t.leg.onRequest}
      </li>
    </ul>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-5">
      <legend className="text-sm font-medium uppercase tracking-[0.18em] text-primary">
        {legend}
      </legend>
      {children}
    </fieldset>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-5 sm:grid-cols-2">{children}</div>;
}

const SUCCESS_T: Record<Lang, {
  heading: string;
  intro: string;
  summary: string;
  dates: string;
  nights: (n: number) => string;
  bedrooms: string;
  guests: string;
  totalGuests: string;
  adults: string;
  children: string;
  babies: string;
  estTotal: string;
  nextSteps: string;
  nextStepsBody: string;
  reference: string;
  addToCalendar: string;
  addGoogle: string;
  addIcal: string;
  eventTitle: string;
}> = {
  nl: {
    heading: "Boekingsaanvraag bevestigd",
    intro: "We hebben je aanvraag goed ontvangen. Je krijgt een bevestiging per e-mail.",
    summary: "Samenvatting van je reservering",
    dates: "Aankomst → vertrek",
    nights: (n) => `${n} nacht${n === 1 ? "" : "en"}`,
    bedrooms: "Slaapkamers",
    guests: "Gasten",
    totalGuests: "Totaal aantal gasten",
    adults: "volwassenen",
    children: "kinderen",
    babies: "baby's",
    estTotal: "Geraamd totaal",
    nextSteps: "Wat nu?",
    nextStepsBody: "We bevestigen de beschikbaarheid binnen 24 uur en sturen daarna de link om het voorschot te betalen.",
    reference: "Referentie",
    addToCalendar: "Toevoegen aan kalender",
    addGoogle: "Google Calendar",
    addIcal: "Apple / iCal (.ics)",
    eventTitle: "Verblijf Casa Mi Pajobe",
  },
  fr: {
    heading: "Demande de réservation confirmée",
    intro: "Nous avons bien reçu votre demande. Vous recevrez une confirmation par e-mail.",
    summary: "Récapitulatif de votre réservation",
    dates: "Arrivée → départ",
    nights: (n) => `${n} nuit${n === 1 ? "" : "s"}`,
    bedrooms: "Chambres",
    guests: "Personnes",
    totalGuests: "Nombre total de personnes",
    adults: "adultes",
    children: "enfants",
    babies: "bébés",
    estTotal: "Total estimé",
    nextSteps: "Et maintenant ?",
    nextStepsBody: "Nous confirmons la disponibilité dans les 24 heures et envoyons ensuite le lien pour régler l’acompte.",
    reference: "Référence",
    addToCalendar: "Ajouter au calendrier",
    addGoogle: "Google Agenda",
    addIcal: "Apple / iCal (.ics)",
    eventTitle: "Séjour Casa Mi Pajobe",
  },
  de: {
    heading: "Buchungsanfrage bestätigt",
    intro: "Wir haben Ihre Anfrage erhalten. Sie erhalten eine Bestätigung per E-Mail.",
    summary: "Übersicht Ihrer Reservierung",
    dates: "Anreise → Abreise",
    nights: (n) => `${n} Nacht${n === 1 ? "" : "e"}`,
    bedrooms: "Schlafzimmer",
    guests: "Gäste",
    totalGuests: "Gesamtanzahl Gäste",
    adults: "Erwachsene",
    children: "Kinder",
    babies: "Babys",
    estTotal: "Geschätzter Gesamtbetrag",
    nextSteps: "Wie geht es weiter?",
    nextStepsBody: "Wir bestätigen die Verfügbarkeit innerhalb von 24 Stunden und senden anschließend den Link zur Zahlung der Anzahlung.",
    reference: "Referenz",
    addToCalendar: "Zum Kalender hinzufügen",
    addGoogle: "Google Kalender",
    addIcal: "Apple / iCal (.ics)",
    eventTitle: "Aufenthalt Casa Mi Pajobe",
  },
  en: {
    heading: "Booking request confirmed",
    intro: "We received your request. A confirmation has been sent to your inbox.",
    summary: "Reservation summary",
    dates: "Arrival → departure",
    nights: (n) => `${n} night${n === 1 ? "" : "s"}`,
    bedrooms: "Bedrooms",
    guests: "Guests",
    totalGuests: "Total guests",
    adults: "adults",
    children: "children",
    babies: "babies",
    estTotal: "Estimated total",
    nextSteps: "What's next?",
    nextStepsBody: "We will confirm availability within 24 hours and send the link to pay the deposit.",
    reference: "Reference",
    addToCalendar: "Add to calendar",
    addGoogle: "Google Calendar",
    addIcal: "Apple / iCal (.ics)",
    eventTitle: "Stay at Casa Mi Pajobe",
  },
};

// Calendar helpers — all-day event spanning check-in (inclusive) to check-out (exclusive).
function toCalDate(iso: string) {
  // YYYY-MM-DD -> YYYYMMDD
  return iso.replace(/-/g, "");
}
function googleCalendarUrl(opts: {
  title: string;
  checkIn: string;
  checkOut: string;
  details: string;
  location: string;
}) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: opts.title,
    dates: `${toCalDate(opts.checkIn)}/${toCalDate(opts.checkOut)}`,
    details: opts.details,
    location: opts.location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
function buildIcs(opts: {
  title: string;
  checkIn: string;
  checkOut: string;
  details: string;
  location: string;
  reference: string;
}) {
  const dtStamp =
    new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const esc = (s: string) =>
    s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Casa Mi Pajobe//Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${opts.reference}@casamipajobe.com`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;VALUE=DATE:${toCalDate(opts.checkIn)}`,
    `DTEND;VALUE=DATE:${toCalDate(opts.checkOut)}`,
    `SUMMARY:${esc(opts.title)}`,
    `DESCRIPTION:${esc(opts.details)}`,
    `LOCATION:${esc(opts.location)}`,
    "TRANSP:TRANSPARENT",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}
function downloadIcs(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function SuccessCard({
  lang,
  t,
  status,
}: {
  lang: Lang;
  t: BookT;
  status: Extract<Status, { kind: "success" }>;
}) {
  const s = SUCCESS_T[lang];
  const { summary } = status;
  const fmtDate = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString(lang, {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-primary/30 bg-primary/5 shadow-sm">
      <div className="flex items-start gap-4 border-b border-primary/20 bg-primary/10 p-6">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="size-5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-foreground">{s.heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{s.intro}</p>
          <p className="mt-2 text-sm text-foreground">
            {s.reference}: <span className="font-mono font-medium">{status.reference}</span>
          </p>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-sm font-medium uppercase tracking-[0.18em] text-primary">
          {s.summary}
        </h3>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border/60 bg-background p-4">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {s.dates}
            </dt>
            <dd className="mt-1 text-sm font-medium text-foreground">
              {fmtDate(summary.checkIn)} → {fmtDate(summary.checkOut)}
            </dd>
            <dd className="text-xs text-muted-foreground">{s.nights(summary.nights)}</dd>
          </div>
          <div className="rounded-lg border border-border/60 bg-background p-4">
            <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {s.bedrooms}
            </dt>
            <dd className="mt-1 text-sm font-medium text-foreground">{summary.bedrooms}</dd>
          </div>
          <div className="rounded-lg border border-primary/30 bg-primary/10 p-4 sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wide text-primary">
              {s.totalGuests}
            </dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">{summary.totalGuests}</dd>
            <dd className="mt-1 text-xs text-muted-foreground">
              {summary.adults} {s.adults}
              {summary.children > 0 && ` · ${summary.children} ${s.children}`}
              {summary.babies > 0 && ` · ${summary.babies} ${s.babies}`}
            </dd>
          </div>
          {summary.grandTotal != null && (
            <div className="rounded-lg border border-border/60 bg-background p-4 sm:col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {s.estTotal}
              </dt>
              <dd className="mt-1 text-lg font-semibold text-foreground">
                {formatEUR(summary.grandTotal)}
              </dd>
            </div>
          )}
        </dl>
        <div className="mt-6 rounded-lg border border-border/60 bg-background p-4">
          <p className="text-sm font-medium text-foreground">{s.nextSteps}</p>
          <p className="mt-1 text-sm text-muted-foreground">{s.nextStepsBody}</p>
        </div>
        <div className="mt-6">
          <p className="text-sm font-medium text-foreground">{s.addToCalendar}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            {(() => {
              const title = `${s.eventTitle} — ${status.reference}`;
              const location = "Casa Mi Pajobe, Pinoso, Alicante, Spain";
              const details = [
                `${s.reference}: ${status.reference}`,
                `${s.dates}: ${summary.checkIn} → ${summary.checkOut} (${s.nights(summary.nights)})`,
                `${s.totalGuests}: ${summary.totalGuests}`,
              ].join("\n");
              const gUrl = googleCalendarUrl({
                title,
                checkIn: summary.checkIn,
                checkOut: summary.checkOut,
                details,
                location,
              });
              const onIcs = () => {
                const ics = buildIcs({
                  title,
                  checkIn: summary.checkIn,
                  checkOut: summary.checkOut,
                  details,
                  location,
                  reference: status.reference,
                });
                downloadIcs(`casamipajobe-${status.reference}.ics`, ics);
              };
              return (
                <>
                  <a
                    href={gUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
                  >
                    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {s.addGoogle}
                  </a>
                  <button
                    type="button"
                    onClick={onIcs}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
                  >
                    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    {s.addIcal}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  maxLength,
  autoComplete,
  value,
  onChange,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  maxLength?: number;
  autoComplete?: string;
  value?: string;
  onChange?: (next: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </span>
      <input
        type={type}
        name={name}
        required={required}
        maxLength={maxLength}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
    </label>
  );
}

function CountryField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">
        {label} <span className="text-primary">*</span>
      </span>
      <input
        type="text"
        name="country"
        required
        maxLength={80}
        list="country-dial-list"
        autoComplete="country-name"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
      <datalist id="country-dial-list">
        {COUNTRY_DIAL_CODES.map((c) => (
          <option key={c.code} value={c.name}>
            {c.flag} {c.dial}
          </option>
        ))}
      </datalist>
    </label>
  );
}

function PhoneField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">
        {label} <span className="text-primary">*</span>
      </span>
      <input
        type="tel"
        name="phone"
        required
        maxLength={40}
        autoComplete="tel"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      />
    </label>
  );
}

function ControlledSelect({
  label,
  name,
  options,
  required,
  value,
  onValueChange,
}: {
  label: string;
  name: string;
  options: number[];
  required?: boolean;
  value: number;
  onValueChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </span>
      <select
        name={name}
        required={required}
        value={value}
        onChange={(e) => onValueChange(Number(e.currentTarget.value))}
        className="mt-2 w-full rounded-lg border border-input bg-background px-4 py-3 text-base outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/30"
      >
        {options.map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </label>
  );
}

function QuoteCard({
  t,
  quote,
  bedrooms,
  guests,
  adults,
  children,
  babies,
  linenTotal,
  babyPackTotal,
  grandTotal,
  overlaps,
}: {
  t: BookT;
  quote: ReturnType<typeof computeQuote> | null;
  bedrooms: Beds;
  guests: number;
  adults: number;
  children: number;
  babies: number;
  linenTotal: number;
  babyPackTotal: number;
  grandTotal: number | null;
  overlaps: boolean;
}) {
  if (!quote) {
    return (
      <div className="rounded-xl border border-dashed border-border/70 bg-background/60 p-5 text-sm text-muted-foreground">
        {t.q.placeholder}
      </div>
    );
  }

  if (overlaps) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-5 text-sm text-destructive">
        {t.q.overlap}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="text-sm font-medium uppercase tracking-wide text-primary">{t.q.title}</p>
          <p className="text-xs text-muted-foreground">
            {t.q.summary({ nights: quote.nights, bedrooms, adults, children, babies })}
          </p>
        </div>

        <ul className="mt-4 divide-y divide-border/60 text-sm">
          {quote.segments.map((s) => (
            <li key={s.label} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
              <div>
                <p className="font-medium text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground">
                  {t.q.nights(s.nights)}
                  {s.weeklyRate != null && (
                    <> · {formatEUR(s.weeklyRate)} {t.q.perWeek}</>
                  )}
                </p>
              </div>
              <p className="text-lg font-semibold tabular-nums text-foreground">
                {s.subtotal != null ? formatEUR(s.subtotal) : t.q.onRequest}
              </p>
            </li>
          ))}
          {quote.outsideRateCard > 0 && (
            <li className="py-2 text-xs text-muted-foreground">
              {t.q.outsideRateCard(quote.outsideRateCard)}
            </li>
          )}
        </ul>

        <div className="mt-4 space-y-1.5 border-t border-border/60 pt-3 text-sm">
          <Line
            t={t}
            label={t.q.rentalLabel}
            value={quote.rentalSubtotal != null ? formatEUR(quote.rentalSubtotal) : t.q.onRequest}
            hint={t.q.rentalHint}
            emphasize
          />
          {quote.discount && (
            <div className="flex items-baseline justify-between gap-2 py-0.5 text-sm">
              <span className="font-medium text-green-700 dark:text-green-400">
                {quote.discount.label} (−{quote.discount.percent}%)
              </span>
              <span className="tabular-nums font-semibold text-green-700 dark:text-green-400">
                −{formatEUR(quote.discount.amount)}
              </span>
            </div>
          )}
          <Line
            t={t}
            label={t.q.cleaningLabel}
            value={formatEUR(FIXED_EXTRAS.cleaning)}
            hint={t.q.cleaningHint}
          />
          <Line
            t={t}
            label={t.q.linenLabel(guests, FIXED_EXTRAS.linenPerPerson)}
            value={formatEUR(linenTotal)}
            hint={t.q.linenHint}
          />
          {babyPackTotal > 0 && (
            <Line
              t={t}
              label={t.q.babyPackLabel}
              value={formatEUR(babyPackTotal)}
              hint={t.q.babyPackHint}
            />
          )}
        </div>

        <div className="mt-3 flex items-baseline justify-between border-t border-border/60 pt-3">
          <p className="text-sm font-semibold text-foreground">{t.q.estimatedTotal}</p>
          <p className="text-2xl font-semibold tabular-nums text-foreground">
            {grandTotal != null ? formatEUR(grandTotal) : t.q.onRequest}
          </p>
        </div>

        <div className="mt-3 border-t border-border/60 pt-3">
          <Line
            t={t}
            label={t.q.depositLabel}
            value={formatEUR(FIXED_EXTRAS.deposit)}
            muted
            hint={t.q.depositHint}
          />
          <p className="mt-1 text-xs text-muted-foreground">{t.q.depositFooter}</p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{t.q.excludes}</p>
      </TooltipProvider>
    </div>
  );
}

function Line({
  t,
  label,
  value,
  muted,
  hint,
  emphasize,
}: {
  t: BookT;
  label: string;
  value: string;
  muted?: boolean;
  hint?: string;
  emphasize?: boolean;
}) {
  return (
    <div className="flex justify-between gap-3">
      <span
        className={cn(
          "inline-flex items-center gap-1.5",
          muted ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {label}
        {hint && (
          <Tooltip>
            <TooltipTrigger
              type="button"
              aria-label={t.moreInfo(label)}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-[240px] rounded-md bg-foreground px-3 py-2 text-xs leading-snug text-background"
            >
              {hint}
            </TooltipContent>
          </Tooltip>
        )}
      </span>
      <span
        className={cn(
          "tabular-nums",
          muted ? "text-muted-foreground" : "text-foreground",
          emphasize && "text-lg font-semibold",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/** Convenience: a default header pair used by the English /book route. */
export function BookPageHeaderLinks({ lang }: { lang: Lang }) {
  const t = BOOK_T[lang];
  return (
    <>
      {lang === "en" ? (
        <Link to="/" className="font-display text-xl font-semibold">
          Casa Mi Pajobe
        </Link>
      ) : (
        <Link to="/$lang" params={{ lang }} className="font-display text-xl font-semibold">
          Casa Mi Pajobe
        </Link>
      )}
      {lang === "en" ? (
        <Link to="/" className="text-sm text-muted-foreground transition hover:text-foreground">
          {t.backToVilla}
        </Link>
      ) : (
        <Link
          to="/$lang"
          params={{ lang }}
          className="text-sm text-muted-foreground transition hover:text-foreground"
        >
          {t.backToVilla}
        </Link>
      )}
    </>
  );
}

const CONFIRM_T: Record<Lang, {
  title: string;
  intro: string;
  contact: string;
  stay: string;
  guests: string;
  extras: string;
  message: string;
  edit: string;
  confirm: string;
  submitting: string;
  nights: (n: number) => string;
  bedrooms: (n: number) => string;
  adults: (n: number) => string;
  children: (n: number) => string;
  babies: (n: number) => string;
  earlyCheckIn: string;
  lateCheckOut: string;
  totalEst: string;
}> = {
  nl: {
    title: "Controleer je gegevens",
    intro: "Klopt alles? Bevestig om je aanvraag te versturen.",
    contact: "Contactgegevens",
    stay: "Verblijf",
    guests: "Gasten",
    extras: "Extra's",
    message: "Bericht",
    edit: "Aanpassen",
    confirm: "Aanvraag versturen",
    submitting: "Versturen…",
    nights: (n) => `${n} ${n === 1 ? "nacht" : "nachten"}`,
    bedrooms: (n) => `${n} slaapkamers`,
    adults: (n) => `${n} volwassenen`,
    children: (n) => `${n} kinderen`,
    babies: (n) => `${n} baby's`,
    earlyCheckIn: "Vroege check-in aangevraagd",
    lateCheckOut: "Late check-out aangevraagd",
    totalEst: "Geschat totaal",
  },
  fr: {
    title: "Vérifiez vos informations",
    intro: "Tout est correct ? Confirmez pour envoyer votre demande.",
    contact: "Coordonnées",
    stay: "Séjour",
    guests: "Voyageurs",
    extras: "Extras",
    message: "Message",
    edit: "Modifier",
    confirm: "Envoyer la demande",
    submitting: "Envoi…",
    nights: (n) => `${n} ${n === 1 ? "nuit" : "nuits"}`,
    bedrooms: (n) => `${n} chambres`,
    adults: (n) => `${n} adultes`,
    children: (n) => `${n} enfants`,
    babies: (n) => `${n} bébés`,
    earlyCheckIn: "Arrivée anticipée demandée",
    lateCheckOut: "Départ tardif demandé",
    totalEst: "Total estimé",
  },
  de: {
    title: "Bitte prüfe deine Angaben",
    intro: "Alles korrekt? Bestätige, um deine Anfrage zu senden.",
    contact: "Kontaktdaten",
    stay: "Aufenthalt",
    guests: "Gäste",
    extras: "Extras",
    message: "Nachricht",
    edit: "Bearbeiten",
    confirm: "Anfrage senden",
    submitting: "Senden…",
    nights: (n) => `${n} ${n === 1 ? "Nacht" : "Nächte"}`,
    bedrooms: (n) => `${n} Schlafzimmer`,
    adults: (n) => `${n} Erwachsene`,
    children: (n) => `${n} Kinder`,
    babies: (n) => `${n} Babys`,
    earlyCheckIn: "Frühes Check-in angefragt",
    lateCheckOut: "Spätes Check-out angefragt",
    totalEst: "Geschätzte Gesamtsumme",
  },
  en: {
    title: "Review your details",
    intro: "Does everything look right? Confirm to send your inquiry.",
    contact: "Contact details",
    stay: "Stay",
    guests: "Guests",
    extras: "Extras",
    message: "Message",
    edit: "Edit",
    confirm: "Send inquiry",
    submitting: "Sending…",
    nights: (n) => `${n} ${n === 1 ? "night" : "nights"}`,
    bedrooms: (n) => `${n} bedrooms`,
    adults: (n) => `${n} adults`,
    children: (n) => `${n} children`,
    babies: (n) => `${n} babies`,
    earlyCheckIn: "Early check-in requested",
    lateCheckOut: "Late check-out requested",
    totalEst: "Estimated total",
  },
};

function ConfirmInquiryModal({
  lang,
  firstName,
  lastName,
  email,
  country,
  phone,
  checkIn,
  checkOut,
  nights,
  bedrooms,
  adults,
  children,
  babies,
  earlyCheckIn,
  lateCheckOut,
  messageText,
  grandTotal,
  onCancel,
  onConfirm,
  submitting,
}: {
  lang: Lang;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  bedrooms: number;
  adults: number;
  children: number;
  babies: number;
  earlyCheckIn: boolean;
  lateCheckOut: boolean;
  messageText: string;
  grandTotal: number | null;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  const c = CONFIRM_T[lang];
  const localeMap: Record<Lang, string> = { nl: "nl-BE", fr: "fr-BE", de: "de-DE", en: "en-GB" };
  const fmtDate = (iso: string) =>
    iso
      ? new Date(iso + "T00:00:00").toLocaleDateString(localeMap[lang], {
          weekday: "short",
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "";
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-inquiry-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-background p-6 shadow-2xl">
        <h2 id="confirm-inquiry-title" className="text-2xl font-semibold text-foreground">
          {c.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{c.intro}</p>

        <div className="mt-6 space-y-5 text-sm">
          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {c.contact}
            </p>
            <dl className="mt-2 space-y-1 text-foreground">
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Naam</dt><dd className="text-right font-medium">{[firstName, lastName].filter(Boolean).join(" ")}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Email</dt><dd className="text-right font-medium break-all">{email}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Land</dt><dd className="text-right font-medium">{country}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Tel.</dt><dd className="text-right font-medium">{phone}</dd></div>
            </dl>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {c.stay}
            </p>
            <dl className="mt-2 space-y-1 text-foreground">
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Check-in</dt><dd className="text-right font-medium">{fmtDate(checkIn)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Check-out</dt><dd className="text-right font-medium">{fmtDate(checkOut)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-foreground">{c.nights(nights)}</dt><dd className="text-right font-medium">{c.bedrooms(bedrooms)}</dd></div>
            </dl>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {c.guests}
            </p>
            <p className="mt-2 text-foreground">
              {c.adults(adults)} · {c.children(children)} · {c.babies(babies)}
            </p>
          </section>

          {(earlyCheckIn || lateCheckOut) && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {c.extras}
              </p>
              <ul className="mt-2 list-disc pl-5 text-foreground">
                {earlyCheckIn && <li>{c.earlyCheckIn}</li>}
                {lateCheckOut && <li>{c.lateCheckOut}</li>}
              </ul>
            </section>
          )}

          {messageText.trim() && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {c.message}
              </p>
              <p className="mt-2 whitespace-pre-wrap text-foreground">{messageText}</p>
            </section>
          )}

          {grandTotal != null && (
            <section className="rounded-lg border border-border bg-muted/40 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{c.totalEst}</span>
                <span className="text-lg font-semibold text-foreground tabular-nums">{formatEUR(grandTotal)}</span>
              </div>
            </section>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-60"
          >
            {c.edit}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? c.submitting : c.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}